// Create a new CommentManager.jsx file for admin comment management
import { useState, useEffect } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Text,
  Badge,
  Stack,
  useToast,
  useColorModeValue
} from '@chakra-ui/react';
import { adminFunctions } from '../../firebase/adminFunctions';

export default function CommentManager() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Theme colors
  const subtleBg = useColorModeValue('ancient.subtle', 'gray.700');

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    try {
      const result = await adminFunctions.getAllComments();
      setComments(result);
    } catch (error) {
      toast({
        title: "Error loading comments",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await adminFunctions.deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast({
        title: "Comment deleted",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error deleting comment",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleApprove = async (commentId) => {
    try {
      await adminFunctions.approveComment(commentId);
      setComments(comments.map(c => (
        c.id === commentId ? {...c, status: 'approved'} : c
      )));
      toast({
        title: "Comment approved",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error approving comment",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  if (loading) return <Box p={4}><Text>Loading comments...</Text></Box>;

  return (
    <Box p={4} overflowX="auto">
      <Table variant="carved">
        <Thead>
          <Tr>
            <Th>User</Th>
            <Th>Comment</Th>
            <Th>Site</Th>
            <Th>Date</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {comments.map((comment) => (
            <Tr key={comment.id}>
              <Td>
                <Text>{comment.authorName}</Text>
                <Text fontSize="sm" color="ancient.lightStone">{comment.authorEmail}</Text>
              </Td>
              <Td>
                <Text noOfLines={2}>{comment.text}</Text>
                {comment.parentComment && (
                  <Box bg={subtleBg} p={2} mt={2} borderRadius="md">
                    <Text fontSize="sm" opacity={0.8}>
                      Replying to: {comment.parentComment.text}
                    </Text>
                  </Box>
                )}
              </Td>
              <Td>
                <Text>{comment.siteName}</Text>
              </Td>
              <Td>
                <Text>
                  {new Date(comment.createdAt).toLocaleDateString()}
                </Text>
                <Text fontSize="sm" color="ancient.lightStone">
                  {new Date(comment.createdAt).toLocaleTimeString()}
                </Text>
              </Td>
              <Td>
                <Badge 
                  bg={comment.status === 'approved' ? 'ancient.terracotta' : 'ancient.gold'}
                  color="white"
                >
                  {comment.status}
                </Badge>
              </Td>
              <Td>
                <Stack direction="row" spacing={2}>
                  {comment.status !== 'approved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApprove(comment.id)}
                      color="ancient.terracotta"
                      borderColor="ancient.terracotta"
                    >
                      Approve
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    color="ancient.terracotta"
                    onClick={() => handleDelete(comment.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
